import { useEffect, useRef, useState } from "react";
import { Eye, EyeSlash } from "@phosphor-icons/react";

const DOT = "\u2022";
const REVEAL_MS = 1500;

// Password field that shows the last typed character for ~1.5s (like phones do)
// plus an eye toggle to show/hide the whole value. Starts as a native password
// input so browser autofill still works; switches to custom masking on typing.
export default function PasswordInput({ value, onChange, className = "", wrapperClassName = "", ...rest }) {
  const [show, setShow] = useState(false);
  const [custom, setCustom] = useState(false);
  const [revealIdx, setRevealIdx] = useState(-1);
  const timer = useRef(null);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const reveal = (idx) => {
    setRevealIdx(idx);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setRevealIdx(-1), REVEAL_MS);
  };

  const masked = value.split("").map((c, i) => (i === revealIdx ? c : DOT)).join("");
  const display = show ? value : custom ? masked : value;
  const type = !show && !custom ? "password" : "text";

  const handleChange = (e) => {
    const next = e.target.value;
    if (!custom) {
      // Native mode (autofill-friendly). Single-char append = real typing → switch to masked mode.
      if (!show && next.length === value.length + 1 && next.startsWith(value)) {
        setCustom(true);
        reveal(next.length - 1);
      }
      onChange({ target: { value: next } });
      return;
    }
    if (show) { onChange({ target: { value: next } }); return; }
    // Custom masked mode: map the edit on the masked display back onto the real value.
    const prev = masked;
    let start = 0;
    const maxStart = Math.min(prev.length, next.length);
    while (start < maxStart && prev[start] === next[start]) start++;
    let pEnd = prev.length, nEnd = next.length;
    while (pEnd > start && nEnd > start && prev[pEnd - 1] === next[nEnd - 1]) { pEnd--; nEnd--; }
    const inserted = next.slice(start, nEnd);
    const real = value.slice(0, start) + inserted + value.slice(pEnd);
    if (inserted.length > 0) reveal(start + inserted.length - 1);
    else setRevealIdx(-1);
    onChange({ target: { value: real } });
  };

  const testId = rest["data-testid"];
  return (
    <div className={`relative ${wrapperClassName}`}>
      <input {...rest} type={type} value={display} onChange={handleChange}
        autoCapitalize="none" autoCorrect="off" spellCheck={false}
        className={`${className} pr-11`} />
      <button type="button" tabIndex={-1} onClick={() => { setShow((s) => !s); setRevealIdx(-1); }}
        aria-label={show ? "Hide password" : "Show password"}
        data-testid={testId ? `${testId}-toggle` : "password-toggle"}
        className="absolute right-0 top-0 h-full px-3 flex items-center text-slate-400 hover:text-slate-700">
        {show ? <EyeSlash size={20} /> : <Eye size={20} />}
      </button>
    </div>
  );
}
